/**
 * Panel y bloque de comando — piezas compartidas de las pantallas que no son
 * un listado (paneles, secciones de configuración, bloques de ayuda).
 *
 * POR QUÉ EXISTEN
 * ---------------------------------------------------------------------------
 * La sección del agente las tenía escritas **tres veces** (AgentSettings,
 * AgentOperationsPanel y el editor de mapeo), cada una con su idea de tarjeta:
 * `card shadow-sm` de Bootstrap, `rounded border bg-light`, `bg-white`… y con
 * colores en línea (`bg-dark text-light`) que en los skins de cristal dejaban
 * texto ilegible.
 *
 * Aquí solo hay una tabla de estilos y sale de los tokens del sistema:
 *  · `Panel`       — tarjeta que se **apoya** en la página → `--bg-card`.
 *  · `CommandBlock`— bloque monoespaciado con botón de copiar → `--bg-input`.
 *  · `CopyButton`  — el botón de copiar, con acuse «Copiado».
 *
 * Lo que FLOTA (diálogos, cajones, menús) no usa `Panel`: eso va con
 * `--bg-surface`, que es opaco por obligación (ver `docs/ESTANDAR_LISTADOS.md` §11).
 */
import React, { useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

/** Botón que copia un texto al portapapeles, con acuse. */
export function CopyButton({ text, label = 'Copiar' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="list-btn-secondary"
      style={{ padding: '3px 10px', fontSize: '0.74rem', flexShrink: 0 }}
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      title="Copiar al portapapeles"
    >
      {copied
        ? <><CheckIcon style={{ width: 13, height: 13 }} /> Copiado</>
        : <><ClipboardDocumentIcon style={{ width: 13, height: 13 }} /> {label}</>}
    </button>
  );
}

/** Bloque de comando: monoespaciado, con su botón de copiar. */
export function CommandBlock({ command, title, hint }) {
  return (
    <div style={{ marginBottom: title ? 14 : 0 }}>
      {(title || hint) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div>
            {title && (
              <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.86rem', color: 'var(--text-primary)' }}>
                {title}
              </div>
            )}
            {hint && <div className="list-hint">{hint}</div>}
          </div>
          <CopyButton text={command} />
        </div>
      )}
      <div style={{ position: 'relative', marginTop: title ? 6 : 0 }}>
        <pre
          style={{
            margin: 0,
            padding: '10px 12px',
            background: 'var(--bg-input)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)',
            fontSize: '0.76rem',
            lineHeight: 1.55,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {command}
        </pre>
        {!title && (
          <div style={{ position: 'absolute', top: 6, right: 6 }}>
            <CopyButton text={command} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Tarjeta de sección: se apoya en la página, así que usa el cristal de `--bg-card`. */
export function Panel({ title, hint, actions, children, style }) {
  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem 1.15rem',
        marginBottom: 16,
        ...style,
      }}
    >
      {(title || actions) && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: hint ? 2 : 10, flexWrap: 'wrap',
          }}
        >
          {title && (
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 'var(--fw-semibold)', color: 'var(--text-primary)' }}>
              {title}
            </h3>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>
        </div>
      )}
      {hint && <p className="list-hint" style={{ margin: '0 0 10px' }}>{hint}</p>}
      {children}
    </div>
  );
}

export default Panel;
